import type { Collection } from '@prisma/client'
import type { useFetcher } from '@remix-run/react'
import type { SerializeFrom } from '@remix-run/server-runtime'
import {
  createContext,
  use,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'

import type { AmendableAttachmentType } from '~/contexts/MediaManagerContext'
import type { CollectionStatus } from '~/schemas/collection'

import type {
  CollectionAttachments,
  CollectionFormData,
  CollectionItemFormData,
  SerializedCollectionItemWithProduct,
} from './type'
import type { CollectionConfigurationError } from './utils'
import {
  areCollectionConfigurationsEqual,
  calculateCollectionStatus,
  defaultCollectionFormData,
  updateCollectionAttachmentsFormData,
  updateCollectionFormData,
  updateCollectionItemsFormData,
  validateCollectionConfiguration,
  validateCollectionForPublishing,
} from './utils'

type CollectionPatch = {
  type: 'patch'
  payload: Partial<CollectionFormData>
}

// remove or add items to the collection, reorder items
type CollectionItemsPatch = {
  type: 'patch-collection-items'
  payload: CollectionItemFormData[]
}

// remove or add media to the collection, reorder media
type CollectionAttachmentsPatch = {
  type: 'patch-collection-attachments'
  payload: AmendableAttachmentType[]
}

// update a single attachment's properties
type CollectionAttachmentUpdate = {
  type: 'update-collection-attachment'
  payload: {
    attachmentId: string
    updates: Partial<Pick<AmendableAttachmentType, 'title' | 'description'>>
  }
}

type CollectionActions =
  | CollectionPatch
  | CollectionItemsPatch
  | CollectionAttachmentsPatch
  | CollectionAttachmentUpdate

interface CollectionContextType {
  collection: CollectionFormData
  initialCollection: CollectionFormData
  collectionItems: CollectionItemFormData[]
  initialCollectionItems: CollectionItemFormData[]
  collectionAttachments: AmendableAttachmentType[]
  initialCollectionAttachments: AmendableAttachmentType[]
  liveErrors: Map<string, CollectionConfigurationError>
  liveValidationFields: Map<string, Date>
  setLiveValidationFields: React.Dispatch<
    React.SetStateAction<Map<string, Date>>
  >
  isPublishingMode: boolean
  setIsPublishingMode: React.Dispatch<React.SetStateAction<boolean>>
  dispatch: React.Dispatch<CollectionActions>
  hasUnsavedChanges: boolean
  status: CollectionStatus | 'unsaved'
  fetcher: ReturnType<typeof useFetcher>
}

const collectionReducer = (
  state: {
    collection: CollectionFormData
    collectionItems: CollectionItemFormData[]
    collectionAttachments: AmendableAttachmentType[]
  },
  action: CollectionActions
) => {
  switch (action.type) {
    case 'patch': {
      return {
        ...state,
        collection: {
          ...state.collection,
          ...action.payload,
        },
      }
    }

    case 'patch-collection-items': {
      return {
        ...state,
        collectionItems: action.payload,
      }
    }

    case 'patch-collection-attachments': {
      return {
        ...state,
        collectionAttachments: action.payload,
      }
    }

    case 'update-collection-attachment': {
      return {
        ...state,
        collectionAttachments: state.collectionAttachments.map((attachment) =>
          attachment.id === action.payload.attachmentId
            ? { ...attachment, ...action.payload.updates }
            : attachment
        ),
      }
    }

    default:
      return state
  }
}

const CollectionContext = createContext<CollectionContextType | null>(null)

export const useCollectionConfiguration = () => {
  const context = use(CollectionContext)

  if (!context) {
    throw new Error('useCollection must be used within a CollectionProvider')
  }

  return context
}

// Provider Props
interface CollectionConfigurationProviderProps {
  initialCollection?: SerializeFrom<Collection>
  initialCollectionItems?: SerializedCollectionItemWithProduct[]
  initialCollectionAttachments?: CollectionAttachments
  children: React.ReactNode
  fetcher: ReturnType<typeof useFetcher>
}

// Provider Component
export const CollectionConfigurationContextProvider = ({
  initialCollection,
  initialCollectionItems = [],
  initialCollectionAttachments = [],
  fetcher,
  children,
}: CollectionConfigurationProviderProps) => {
  // Initialize state - this will be updated when we receive new data from fetcher
  const [initialCollectionData, setInitialCollectionData] = useState(() => {
    const collection = initialCollection
      ? updateCollectionFormData(initialCollection)
      : defaultCollectionFormData

    const collectionItems = updateCollectionItemsFormData(
      initialCollectionItems
    )

    const collectionAttachments = updateCollectionAttachmentsFormData(
      initialCollectionAttachments
    )

    return {
      collection,
      collectionItems,
      collectionAttachments,
    }
  })

  const [state, dispatch] = useReducer(collectionReducer, initialCollectionData)

  const [liveValidationFields, setLiveValidationFields] = useState(
    new Map<string, Date>()
  )

  const [isPublishingMode, setIsPublishingMode] = useState(false)
  const needsSyncAfterSave = useRef(false)

  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return !areCollectionConfigurationsEqual(
      {
        collection: state.collection,
        collectionItems: state.collectionItems,
        collectionAttachments: state.collectionAttachments,
      },
      {
        collection: initialCollectionData.collection,
        collectionItems: initialCollectionData.collectionItems,
        collectionAttachments: initialCollectionData.collectionAttachments,
      }
    )
  }, [state, initialCollectionData])

  const status = useMemo(() => {
    return calculateCollectionStatus(state.collection)
  }, [state.collection])

  const liveErrors = useMemo(() => {
    if (!liveValidationFields.size) return new Map()

    const errors: Map<string, CollectionConfigurationError> = new Map()

    const validationFunction = isPublishingMode
      ? validateCollectionForPublishing
      : validateCollectionConfiguration

    const allErrors = validationFunction({
      collection: state.collection,
      collectionItems: state.collectionItems,
      status,
    })

    for (const [field] of liveValidationFields.entries()) {
      if (allErrors.has(field)) {
        errors.set(field, allErrors.get(field)!)
      }
    }

    return errors
  }, [
    liveValidationFields,
    state.collection,
    state.collectionItems,
    status,
    isPublishingMode,
  ])

  // Sync with external data changes (initial data or fetcher updates)
  useEffect(() => {
    // Check if we have updated collection data from fetcher
    if (
      fetcher.data &&
      typeof fetcher.data === 'object' &&
      !('errors' in fetcher.data) &&
      !('success' in fetcher.data) &&
      'id' in fetcher.data &&
      fetcher.data.id
    ) {
      const updatedCollection = fetcher.data as SerializeFrom<Collection>
      const updatedCollectionData = updateCollectionFormData(updatedCollection)

      // Update the current state to match the saved state
      dispatch({
        type: 'patch',
        payload: updatedCollectionData,
      })

      // Clear attachments from current state for new collections
      if (!initialCollection) {
        dispatch({
          type: 'patch-collection-attachments',
          payload: [],
        })
      }

      // Set flag to sync initial data after state update
      needsSyncAfterSave.current = true
    }
    // Otherwise, sync with initial collection data
    else if (initialCollection) {
      const initialCollectionData = updateCollectionFormData(initialCollection)
      dispatch({
        type: 'patch',
        payload: initialCollectionData,
      })
      setInitialCollectionData((prev) => ({
        ...prev,
        collection: initialCollectionData,
      }))
    }
  }, [initialCollection, fetcher.data])

  // Sync initial data with current state after successful save
  useEffect(() => {
    if (needsSyncAfterSave.current) {
      // Update initial data to match current state after save
      setInitialCollectionData({
        collection: state.collection,
        collectionItems: state.collectionItems,
        collectionAttachments: state.collectionAttachments,
      })
      needsSyncAfterSave.current = false
    }
  }, [state.collection, state.collectionItems, state.collectionAttachments])

  return (
    <CollectionContext.Provider
      value={{
        collection: state.collection,
        initialCollection: initialCollectionData.collection,
        collectionItems: state.collectionItems,
        initialCollectionItems: initialCollectionData.collectionItems,
        collectionAttachments: state.collectionAttachments,
        initialCollectionAttachments:
          initialCollectionData.collectionAttachments,
        liveErrors,
        liveValidationFields,
        setLiveValidationFields,
        isPublishingMode,
        setIsPublishingMode,
        dispatch,
        hasUnsavedChanges,
        status,
        fetcher,
      }}
    >
      {children}
    </CollectionContext.Provider>
  )
}
